import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {getStringParam, getIntParam, mergeSearch} from '../../../../utils/urlParams';
import {useDispatch, useSelector} from 'react-redux';
import usePageTitle from '../../../../hooks/usePageTitle';
import DataTable from 'react-data-table-component';
import {ExclamationTriangleIcon, ClipboardDocumentIcon, ChevronDownIcon, CalculatorIcon, EnvelopeIcon} from '@heroicons/react/24/outline';
import useSelectedResource from "../../../../hooks/useSelectedResource";
import useManagedResources from "../../../../hooks/useManagedResources";
import StatsCard from '../../../../components/StatsCards';
import {useBreadcrumbs} from '../../../../contexts/BreadcrumbContext';
import PaymentsActionsPanel from '../../../../components/PaymentPageActions';
import CalculatePaymentsModal from '../../../../components/CalculatePaymentsModal';
import PaymentValidationPanel from '../../../../components/PaymentValidationPanel';
import {metricFieldMap} from "../../../../config/user/paymentMetrics";
import {
  calculatePayments,
  clearPaymentRequests,
  fetchPaymentRequests,
  fetchPaymentRequirements,
  fetchPaymentValidations,
  fetchResources,
  fetchUserPayments,
  requestUserPayments,
  selectPaymentFilters,
  selectPaymentRequests,
  selectPaymentRequestsLoading,
  selectPaymentRequirements,
  selectPaymentValidations,
  selectResources,
  selectResourcesLoaded,
  selectUserPayments,
  setPaymentFilters,
} from "../../../../slices/userSlice";
import {IconState} from "../../../../components/IconState";
import {toast} from "react-toastify";
import {getDataTableStyles} from "../../../../styles/datatablesStyles";
import {calculateTop5Share} from "../../../../helpers/paymentHelper";
import {getPaymentTableColumns} from './paymentTableColumns';
import {buildPaymentStats} from './paymentStats';
import {selectBlurSensitive, selectTheme} from "../../../../slices/preferencesSlice";
import {selectAuthIsAdmin} from "../../../../slices/authSlice";

import {fetchPaymentGroups} from "../../../../slices/paymentGroupsSlice";
import {axiosInstance} from "../../../../routes/axiosInstance";
import PaymentEmailPanel from './GroupSendModal';
import {
  buildGroupParticipants,
  buildPaymentEmailTargets,
  defaultPaymentEmailTarget,
} from './groupedRecipients';
import DatatablePagination from '../../../../components/DatatablePagination';
import FormInput from "../../../../components/FormInput";
import MaxErrorBonusBadge from "../../../../components/MaxErrorBonusBadge";

export default function PaymentsPage() {
  usePageTitle('Payments');
  const theme = useSelector(selectTheme);
  const [effectiveDark, setEffectiveDark] = useState(false);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const compute = () => setEffectiveDark(theme === 'dark' || (theme === 'system' && mediaQuery.matches));
    compute();
    const listener = () => theme === 'system' && compute();
    mediaQuery.addEventListener ? mediaQuery.addEventListener('change', listener) : mediaQuery.addListener(listener);
    return () => {
      mediaQuery.removeEventListener ? mediaQuery.removeEventListener('change', listener) : mediaQuery.removeListener(listener);
    };
  }, [theme]);
  const isAdmin = useSelector(selectAuthIsAdmin);
  useBreadcrumbs(
    isAdmin
      ? [{ label: 'Forecasters', href: '/data/resource/participants' }, { label: 'Payments' }]
      : [{ label: 'Payments' }],
    'Monthly prize distribution and payment history'
  );
  const dispatch = useDispatch();
  const currentDate = new Date();
  // Calculate previous month for default view
  const prevMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
  const defaultYear = prevMonthDate.getFullYear();
  const defaultMonth = String(prevMonthDate.getMonth() + 1).padStart(2, '0');

  const selectedResource = useSelectedResource();
  const { managedResources } = useManagedResources();
  const allResources = useSelector(selectResources);
  const resourcesLoaded = useSelector(selectResourcesLoaded);
  const paymentGroupsState = useSelector((state) => state.paymentGroups.groups);
  const paymentGroups = paymentGroupsState.data;
  const blurSensitive = useSelector(selectBlurSensitive);
  const paymentsData = useSelector(selectUserPayments);
  const [emailFilter, setEmailFilter] = useState('');
  const paymentFilters = useSelector(selectPaymentFilters);
  const year = paymentFilters.year ?? defaultYear;
  const month = paymentFilters.month ?? defaultMonth;
  const selectedMetric = paymentFilters.metric;
  const horizonCategory = paymentFilters.horizonCategory ?? 'all';

  const location = useLocation();
  const navigate = useNavigate();
  const urlSeeded = useRef(false);

  // URL -> Redux, once on mount. Seeds before any data fetch effects run.
  useEffect(() => {
    if (urlSeeded.current) return;
    urlSeeded.current = true;
    const search = location.search;
    const updates = {};

    const urlYear = getIntParam(search, 'year', { fallback: null, min: 2000, max: 2100 });
    if (urlYear) updates.year = urlYear;

    const urlMonth = getIntParam(search, 'month', { fallback: null, min: 1, max: 12 });
    if (urlMonth) updates.month = String(urlMonth).padStart(2, '0');

    const urlMetric = getStringParam(search, 'metric', { fallback: null, allowed: ['total', 'deterministic', 'probabilistic'] });
    if (urlMetric) updates.metric = urlMetric;

    const urlHorizon = getStringParam(search, 'horizon', { fallback: null, allowed: ['all', 'intraday', 'd1', 'dn', 'legacy'] });
    if (urlHorizon) updates.horizonCategory = urlHorizon;

    if (Object.keys(updates).length > 0) dispatch(setPaymentFilters(updates));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redux -> URL (replace) so the current view is shareable. Skips until seed is done.
  useEffect(() => {
    if (!urlSeeded.current) return;
    const next = mergeSearch(location.search, {
      year: year || null,
      month: month ? Number(month) : null,
      metric: selectedMetric && selectedMetric !== 'total' ? selectedMetric : null,
      horizon: horizonCategory && horizonCategory !== 'all' ? horizonCategory : null,
    });
    if (next !== location.search) {
      navigate({ search: next }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, selectedMetric, horizonCategory]);

  const [showCalculateModal, setShowCalculateModal] = useState(false);

  // Resources the user can act on:
  //   - Superusers see everything (they're authorized on every resource).
  //   - Everyone else is filtered to resources they own or co-manage via
  //     UserResourceManager. Without this fallback for superusers, admins
  //     who never explicitly added themselves as a manager (e.g. seeded
  //     resources that bypassed the post_save signal) saw an empty selector.
  const managedResourceIds = new Set(managedResources.map(r => r.resourceId || r.id));
  const resourcesWithActiveStatus = isAdmin
    ? allResources
    : allResources.filter(r => managedResourceIds.has(r.id));
  const [appliedYear, setAppliedYear] = useState(year);
  const [appliedMonth, setAppliedMonth] = useState(month);

  const filteredPayments = paymentsData.filter(p =>
    (p.email || '').toLowerCase().includes(emailFilter.toLowerCase())
  );

  // Check if any email has been sent for the current payment list
  const hasAnyEmailSent = paymentsData.some(payment => payment.email_sent);
  const [showEmailPanel, setShowEmailPanel] = useState(false);
  const [isSendingPayments, setIsSendingPayments] = useState(false);
  const [sendTargetKey, setSendTargetKey] = useState('');
  // Calculated status per bundle member, filled when the email panel opens.
  // A missing key means "still checking" (the panel keeps send disabled).
  const [siblingStatus, setSiblingStatus] = useState({});
  // Payment-list rows per bundle member, from the same probe. The grouped
  // recipient union needs both the user id and group-wide email_sent state:
  // using ids alone can add a sibling's already-sent forecaster back into the
  // request and make the backend reject the entire remaining batch.
  const [siblingPayments, setSiblingPayments] = useState({});
  const [sendValidationStatus, setSendValidationStatus] = useState({});
  const [extraCc, setExtraCc] = useState([]);
  const statusRequestId = useRef(0);
  const existingRequests = useSelector(selectPaymentRequests);
  const paymentRequestsLoading = useSelector(selectPaymentRequestsLoading);
  const paymentRequirements = useSelector(selectPaymentRequirements);
  const paymentValidations = useSelector(selectPaymentValidations);
  const viewKey = selectedResource
    ? `${selectedResource.id}|${year}|${Number(month)}`
    : null;
  const periodLabel = new Date(year, Number(month) - 1, 1)
    .toLocaleString('en-GB', {month: 'long', year: 'numeric'});
  const runValidations = () => {
    if (!selectedResource) return;
    dispatch(fetchPaymentValidations({
      year, month, resource: selectedResource.id,
    }));
  };
  const hasPaymentSubmitted = !paymentRequestsLoading && existingRequests !== null && existingRequests.length > 0;
  const [showCopyMenu, setShowCopyMenu] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  const copyToClipboard = (format) => {
    const separator = format === 'csv' ? ',' : '\t';
    const fields = metricFieldMap[selectedMetric];

    // Headers
    const headers = ['Email', 'Rank', 'Outstanding', 'Month Payment', 'Total Payment', 'Email Sent'];

    // Format rows
    const rows = filteredPayments.map(row => {
      const email = blurSensitive ? '***' : row.email;
      const rank = row[fields.rank] ? row[fields.rank].toFixed(2) : 'N/A';
      const outstanding = row[fields.outstanding]?.toFixed(2) ?? '0.00';
      const monthPay = row[fields.month]?.toFixed(2) ?? '0.00';
      const totalPay = row[fields.total]?.toFixed(2) ?? '0.00';
      const emailSent = row.email_sent ? 'Yes' : 'No';

      return [email, rank, outstanding, monthPay, totalPay, emailSent];
    });

    // Build output
    const output = [
      headers.join(separator),
      ...rows.map(row => row.join(separator))
    ].join('\n');

    navigator.clipboard.writeText(output).then(() => {
      toast.success(`Copied ${filteredPayments.length} rows to clipboard!`);
      setShowCopyMenu(false);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  const loadPaymentsData = () => {
    if (!selectedResource) return;
    dispatch(clearPaymentRequests());
    dispatch(fetchPaymentRequests({year, month, resource: selectedResource.id}));
    dispatch(fetchUserPayments({year, month, selectedMetric, resource: selectedResource.id, horizonCategory}));
    dispatch(fetchPaymentRequirements({year, month, resource: selectedResource.id}));
    // Update applied filters to reflect what's actually shown
    setAppliedYear(year);
    setAppliedMonth(month);
  };

  // Selecting a resource or a period reloads the table right away: there is no
  // separate "apply" step in this view. The payment track is a client-side
  // column switch, so it needs no refetch.
  useEffect(() => {
    loadPaymentsData();
  }, [selectedResource, year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when the horizon category changes (cheap, backend-side filter).
  useEffect(() => {
    if (!selectedResource) return;
    dispatch(fetchUserPayments({
      year, month, resource: selectedResource.id, horizonCategory
    }));
  }, [horizonCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHorizonChange = (value) => dispatch(setPaymentFilters({horizonCategory: value}));
  const HORIZON_OPTIONS = [
    {value: 'all', label: 'All horizons'},
    {value: 'intraday', label: 'Intraday'},
    {value: 'd1', label: 'D+1'},
    {value: 'dn', label: 'D+N'},
    {value: 'legacy', label: 'Legacy (Pre June 26)'},
  ];

  // Fetch the global resources list. Superusers get everything (they're
  // authorized on every resource); other users only need it once they have
  // managed-resource entries to filter against.
  useEffect(() => {
    if (allResources.length === 0 && (isAdmin || managedResources.length > 0)) {
      dispatch(fetchResources());
    }
  }, [isAdmin, managedResources, allResources.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Payment groups drive the grouped send: one email per forecaster covering
  // every market in the group. Superuser-only endpoint.
  useEffect(() => {
    if (isAdmin) dispatch(fetchPaymentGroups());
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Email sending is a payment-scope operation, independent of the resource
  // currently shown in the informative table. Grouped resources can only be
  // sent through their group; ungrouped resources remain standalone scopes.
  // Never briefly expose a grouped market as a standalone send target while
  // the group catalogue is still loading (that would bypass consolidation).
  const sendTargets = paymentGroupsState.status === 'succeeded' && resourcesLoaded
    ? buildPaymentEmailTargets(paymentGroups, allResources)
    : [];
  const sendTarget = sendTargets.find((target) => target.key === sendTargetKey) || null;
  const group = sendTarget?.group || null;
  // Full membership, inactive members included: the backend only drops an
  // inactive member when it has nothing calculated for the month.
  const bundleResources = sendTarget?.resources || [];
  const monthLabel = new Date(year, Number(month) - 1, 1)
    .toLocaleString('en-GB', {month: 'long'});

  // One /user/payment/list probe per bundle member: non-empty means the
  // month is calculated for that market. Keep each row's email_sent flag so
  // users mailed through any sibling are excluded from the whole next send.
  const probeMemberPayments = async (resourceId) => {
    const response = await axiosInstance.get(
      `/user/payment/list?year=${year}&month=${month}&track=total&resource=${resourceId}`);
    return (response.data?.data ?? []).filter((row) => row.user);
  };

  const probeMemberValidation = async (resourceId) => {
    const response = await axiosInstance.get(
      `/user/payment/validation-report?year=${year}&month=${Number(month)}&resource=${resourceId}`);
    return response.data?.data ?? null;
  };

  const loadSiblingStatus = (members) => {
    const requestId = statusRequestId.current + 1;
    statusRequestId.current = requestId;
    setSiblingStatus({});
    setSiblingPayments({});
    // Validation is an explicit operator action. Reset it whenever the scope
    // changes so a report from another group/month can never unlock sending.
    setSendValidationStatus({});
    members.forEach((member) => {
      probeMemberPayments(member.id)
        .then((payments) => {
          if (statusRequestId.current !== requestId) return;
          setSiblingStatus((prev) => ({...prev, [member.id]: payments.length > 0}));
          setSiblingPayments((prev) => ({...prev, [member.id]: payments}));
        })
        .catch(() => {
          if (statusRequestId.current !== requestId) return;
          // Record the failure so the panel shows "Check failed" instead of
          // an endless "Checking...", while still blocking the send.
          setSiblingStatus((prev) => ({...prev, [member.id]: 'error'}));
          toast.error(`Could not check calculated payments for ${member.name}.`);
        });
    });
  };

  const runSendValidations = async () => {
    const requestId = statusRequestId.current;
    const includedResources = bundleResources.filter((resource) => !(
      resource.is_active === false && siblingStatus[resource.id] === false
    ));
    if (includedResources.length === 0) return;

    setSendValidationStatus(Object.fromEntries(
      includedResources.map((resource) => [resource.id, 'running'])
    ));

    const results = await Promise.allSettled(
      includedResources.map(async (resource) => ({
        resource,
        report: await probeMemberValidation(resource.id),
      }))
    );
    if (statusRequestId.current !== requestId) return;

    const nextStatus = {};
    results.forEach((result, index) => {
      const resource = includedResources[index];
      nextStatus[resource.id] = result.status === 'fulfilled' && result.value.report
        ? result.value.report
        : 'error';
    });
    setSendValidationStatus(nextStatus);

    const failedRequests = results.filter((result) => result.status === 'rejected').length;
    const failedChecks = Object.values(nextStatus).filter((report) =>
      report !== 'error' && report?.summary?.fail > 0).length;
    if (failedRequests > 0) {
      toast.error(`Validation could not be completed for ${failedRequests} market${failedRequests === 1 ? '' : 's'}.`);
    } else if (failedChecks > 0) {
      toast.error(`Payment validation failed for ${failedChecks} market${failedChecks === 1 ? '' : 's'}. Fix the results before sending.`);
    } else {
      toast.success('Payment validations passed. Emails are ready to send.');
    }
  };

  const groupParticipants = buildGroupParticipants(
    siblingPayments, bundleResources);
  const failedValidationMarkets = bundleResources.filter((resource) =>
    sendValidationStatus[resource.id]?.summary?.fail > 0);
  const warningValidationMarkets = bundleResources.filter((resource) =>
    sendValidationStatus[resource.id]?.summary?.warn > 0);
  const sendWarnings = [
    ...(failedValidationMarkets.length > 0 ? [{
      type: 'error',
      label: 'Payment validation failed',
      description: `Failed checks on ${failedValidationMarkets.map((resource) => resource.name).join(', ')}. Review them before sending.`,
    }] : []),
    ...(warningValidationMarkets.length > 0 ? [{
      type: 'warning',
      label: 'Payment validation warnings',
      description: `Warnings found on ${warningValidationMarkets.map((resource) => resource.name).join(', ')}.`,
    }] : []),
  ];

  const monthPayment = paymentsData.reduce((sum, p) => sum + p.month_payment, 0);
  const totalOutstanding = paymentsData.reduce((sum, p) => sum + p.outstanding_payment, 0);
  const {top5SharePercent, performanceNote, performanceColor} = calculateTop5Share(paymentsData, monthPayment);

  const stats = buildPaymentStats(
    monthPayment,
    totalOutstanding,
    top5SharePercent,
    performanceNote,
    performanceColor,
    paymentRequestsLoading,
    hasPaymentSubmitted);

  // const columns = getPaymentTableColumns(selectedMetric);
  const columns = getPaymentTableColumns(selectedMetric, blurSensitive);

  // The shared styles set the responsive wrapper to `overflow: visible`, which
  // the card's `overflow-hidden` then clips: on narrow laptops the last column
  // (Email Sent) was cut off and unreachable. Scroll it horizontally instead.
  const baseTableStyles = getDataTableStyles(effectiveDark);
  const tableStyles = {
    ...baseTableStyles,
    responsiveWrapper: {
      ...baseTableStyles.responsiveWrapper,
      style: {
        ...baseTableStyles.responsiveWrapper.style,
        overflowX: 'auto',
        overflowY: 'hidden',
      },
    },
  };

  const handleMetricChange = (value) => dispatch(setPaymentFilters({metric: value}));
  const handleYearChange = (value) => dispatch(setPaymentFilters({year: value}));
  const handleMonthChange = (value) => dispatch(setPaymentFilters({month: value}));

  function pollPayments(year, month, resourceId, retries = 5,
                        expectedUserIds = null, firstRequest = true) {

    if (firstRequest) {
      dispatch(fetchPaymentRequests({year, month, resource: resourceId}));
    }

    if (retries <= 0) return;

    dispatch(fetchUserPayments({year, month, resource: resourceId, horizonCategory}))
      .unwrap()
      .then((data) => {
        const expected = expectedUserIds ? new Set(expectedUserIds) : null;
        const anyPending = data.some((payment) =>
          (!expected || expected.has(payment.user)) && !payment.email_sent);
        if (anyPending) {
          setTimeout(() => {
            pollPayments(year, month, resourceId, retries - 1,
              expectedUserIds, false);
          }, 5000);
        }
      });
  }

  const defaultSendTarget = () => {
    return defaultPaymentEmailTarget(sendTargets, selectedResource?.id);
  };

  const selectSendTarget = (targetKey) => {
    const target = sendTargets.find((candidate) => candidate.key === targetKey);
    if (!target) return;
    setSendTargetKey(target.key);
    setExtraCc([]);
    loadSiblingStatus(target.resources);
  };

  const handlePayment = () => {
    if (isSendingPayments) return;
    const target = defaultSendTarget();
    if (!target) {
      toast.error('No payment groups or standalone resources are available.');
      return;
    }
    selectSendTarget(target.key);
    setShowEmailPanel(true);
  }

  const sendPayments = async (userList, allowResend = false) => {
    if (isSendingPayments) return;
    if (!sendTarget || userList.length === 0) return;
    setIsSendingPayments(true);
    const toastId = toast.loading('Sending payment details to users...');

    try {
      await dispatch(requestUserPayments({
        year,
        month,
        group: sendTarget.group?.id,
        resource: sendTarget.group ? undefined : sendTarget.resources[0]?.id,
        extraCc,
        userList,
        allowResend,
      })).unwrap();

      toast.update(toastId, {
        render: "Payment details sent successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      setExtraCc([]);
      if (sendTarget.resources.some((resource) => resource.id === selectedResource?.id)) {
        pollPayments(year, month, selectedResource.id, 24, userList);
      }
    } catch (error) {
      // 409 answers with a plain string body; the 400 completeness gate
      // answers with a field-keyed validation error, already flattened
      // into `message` by the thunk.
      const detail = typeof error?.data?.data === 'string' ? error.data.data : null;
      toast.update(toastId, {
        render: detail || error?.message || "Failed to request payments",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsSendingPayments(false);
    }
  };

  const handleCalculatePayments = async (resources = null, ignoreOutstanding = false) => {
    if (isCalculating) return;

    // If no resources provided, fall back to selected resource for backwards compatibility
    const resourceIds = resources || (selectedResource ? [selectedResource.id] : []);
    if (resourceIds.length === 0) {
      toast.error('No resources selected for payment calculation.');
      return;
    }

    setIsCalculating(true);
    const resourceCount = resourceIds.length;
    const toastId = toast.loading(
      `Calculating payments for ${resourceCount} resource${resourceCount > 1 ? 's' : ''}...`
    );

    try {
      const result = await dispatch(calculatePayments({
        resources: resourceIds,
        ignoreOutstanding
      })).unwrap();

      const processed = result?.resources_processed ?? 0;
      const failed = result?.resources_failed ?? 0;

      if (failed > 0 && processed > 0) {
        toast.update(toastId, {
          render: `Payment calculation completed with issues: ${processed} succeeded, ${failed} failed`,
          type: "warning",
          isLoading: false,
          autoClose: 5000,
        });
      } else if (failed > 0) {
        toast.update(toastId, {
          render: `Payment calculation failed for ${resourceCount} resource${resourceCount > 1 ? 's' : ''}`,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      } else {
        toast.update(toastId, {
          render: `Payment calculation completed successfully for ${processed} resource${processed > 1 ? 's' : ''}`,
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      }

      // Refresh payments data after calculation (only if we have a selected resource)
      if (selectedResource) {
        loadPaymentsData();
      }
      // Auto-run the pre-payout validations for the month just
      // calculated (always the previous calendar month) when the
      // selected resource was part of the run.
      if (selectedResource && resourceIds.includes(selectedResource.id)) {
        const rm = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
        dispatch(fetchPaymentValidations({
          year: rm.getFullYear(),
          month: String(rm.getMonth() + 1).padStart(2, '0'),
          resource: selectedResource.id,
        }));
      }
      // Close the modal after successful calculation
      setShowCalculateModal(false);
    } catch (error) {
      // Surface per-resource error detail when present (e.g. "No scores
      // found for resource <id>") rather than the generic fallback.
      let message = error || "Failed to calculate payments";
      if (error && typeof error === 'object') {
        const firstDetailErr = error?.details?.find?.((d) => !d.success)?.error;
        message = firstDetailErr || error.message || JSON.stringify(error);
      }
      toast.update(toastId, {
        render: String(message),
        type: "error",
        isLoading: false,
        autoClose: 6000,
      });
      // Close the modal on error too — leaving it open with the spinner is
      // misleading since the calculation already failed.
      setShowCalculateModal(false);
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="space-y-6">
      <PaymentEmailPanel
        open={showEmailPanel}
        targets={sendTargets}
        selectedTargetKey={sendTargetKey}
        onTargetChange={selectSendTarget}
        group={group}
        resources={bundleResources}
        participants={groupParticipants}
        payoutThreshold={sendTarget?.payoutThreshold}
        siblingStatus={siblingStatus}
        validationStatus={sendValidationStatus}
        onRunValidations={runSendValidations}
        warnings={sendWarnings}
        marketOwnerCc={sendTarget?.marketOwnerCc || ''}
        extraRecipients={extraCc}
        onAddCc={(email) => setExtraCc([...extraCc, email])}
        onRemoveCc={(email) => setExtraCc(extraCc.filter((x) => x !== email))}
        onCancel={() => {
          // Extra CC addresses are per send: cancelling drops them.
          setShowEmailPanel(false);
          setExtraCc([]);
        }}
        onConfirm={(userList, allowResend) => {
          setShowEmailPanel(false);
          sendPayments(userList, allowResend).then();
        }}
        sending={isSendingPayments}
        monthLabel={monthLabel}
        year={year}
      />

      {/* Stats + Filters — single card with vertical dividers */}
      <div className="bg-surface dark:bg-gray-870 rounded-lg border border-gray-200/70 dark:border-gray-600/70 shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-gray-100 dark:divide-gray-700">
          <StatsCard stats={stats} divided />
          <div className="sm:w-[440px] sm:flex-shrink-0 px-5 py-4 flex flex-col justify-center gap-3">
            <PaymentsActionsPanel
              year={year}
              month={month}
              appliedYear={appliedYear}
              appliedMonth={appliedMonth}
              paymentRequirements={paymentRequirements}
              hasPaymentsData={paymentsData.length > 0}
              onYearChange={handleYearChange}
              onMonthChange={handleMonthChange}
              options={Object.keys(metricFieldMap)}
              selectedOption={selectedMetric}
              onOptionChange={(e) => handleMetricChange(e.target.value)}
              bare
            />
            {selectedResource && (
              <div className="grid grid-cols-2 gap-2">
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => setShowCalculateModal(true)}
                    disabled={isCalculating || hasAnyEmailSent}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    {isCalculating ? (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <CalculatorIcon className="h-4 w-4" />
                    )}
                    {isCalculating ? 'Calculating...' : 'Calculate Payments'}
                  </button>
                  {(isCalculating || hasAnyEmailSent) && (
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-64 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 pointer-events-none z-[200]">
                      {hasAnyEmailSent ? 'Payment calculation disabled: emails have already been sent for this period' : 'Calculating payments...'}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={handlePayment}
                    disabled={isSendingPayments || sendTargets.length === 0}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <EnvelopeIcon className="h-4 w-4" />
                    Send emails
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {isAdmin && selectedResource && (
        <>
          <MaxErrorBonusBadge
            payments={paymentsData}
            blurSensitive={blurSensitive}
            metric={selectedMetric}
            horizonCategory={horizonCategory}
          />
          <PaymentValidationPanel
            validations={paymentValidations}
            viewKey={viewKey}
            onRun={runValidations}
            periodLabel={periodLabel}
          />
        </>
      )}

      {/* Horizon breakdown selector */}
      <div className="bg-surface dark:bg-gray-870 rounded-lg border border-gray-200/70 dark:border-gray-600/70 shadow-sm px-5 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">
            Horizon:
          </span>
          {HORIZON_OPTIONS.map((opt) => {
            const active = horizonCategory === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleHorizonChange(opt.value)}
                className={
                  "px-3 py-1.5 rounded-full text-sm font-medium transition-colors border " +
                  (active
                    ? "bg-brand-600 text-white border-brand-600"
                    : "bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600")
                }
              >
                {opt.label}
              </button>
            );
          })}
          {horizonCategory !== 'all' && (
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
              Totals reflect the selected horizon only.
            </span>
          )}
        </div>
      </div>

      {/* Forecasters section */}
      {paymentsData.length === 0 ? (
        <div className="w-full py-20">
          <div className="flex flex-col items-center justify-center text-center">
            <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 dark:text-gray-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No payments data available
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md">
              No payment records found for the selected period. Try selecting a different month or year.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Mobile layout */}
          <div className="sm:hidden space-y-3">
            <div className="p-4 bg-surface dark:bg-gray-870 rounded-lg border border-gray-200/70 dark:border-gray-600/70 shadow-sm space-y-3">
              <FormInput
                type="text"
                leftIcon="email"
                placeholder="Search by email..."
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                size="medium"
              />
              <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowCopyMenu(!showCopyMenu)}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                    Copy
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                  {showCopyMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowCopyMenu(false)} />
                      <div className="absolute left-0 mt-1 w-48 bg-white dark:bg-gray-870 rounded-lg shadow-lg border border-gray-200/70 dark:border-gray-600/70 z-20">
                        <button onClick={() => copyToClipboard('tsv')} className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg">Tab-separated (Excel)</button>
                        <button onClick={() => copyToClipboard('csv')} className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg">CSV</button>
                      </div>
                    </>
                  )}
              </div>
            </div>

            {filteredPayments.map((payment) => {
              const fields = metricFieldMap[selectedMetric];
              const rank = payment[fields.rank] ? payment[fields.rank].toFixed(2) : 'N/A';
              const outstanding = payment[fields.outstanding]?.toFixed(2) ?? '0.00';
              const monthPay = payment[fields.month]?.toFixed(2) ?? '0.00';
              const totalPay = payment[fields.total]?.toFixed(2) ?? '0.00';
              return (
                <div
                  key={payment.user}
                  className="p-4 rounded-lg border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-gray-900 dark:text-gray-100 truncate ${blurSensitive ? 'blur-sm select-none' : ''}`}>{payment.email}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Rank: {rank}</span>
                        {payment.email_sent && <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">Email Sent</span>}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><div className="text-xs text-gray-500 dark:text-gray-400">Outstanding</div><div className="text-sm font-semibold text-gray-900 dark:text-gray-100">€{outstanding}</div></div>
                    <div><div className="text-xs text-gray-500 dark:text-gray-400">Month Pay</div><div className="text-sm font-semibold text-gray-900 dark:text-gray-100">€{monthPay}</div></div>
                    <div><div className="text-xs text-gray-500 dark:text-gray-400">Total</div><div className="text-sm font-semibold text-brand-600 dark:text-brand-400">€{totalPay}</div></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: unified card */}
          <div className="hidden sm:block bg-surface dark:bg-gray-870 rounded-lg border border-gray-200/70 dark:border-gray-600/70 shadow-sm overflow-hidden">
            {/* Header + toolbar */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/70 dark:border-gray-600/70">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Forecasters</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{filteredPayments.length} records</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative w-64">
                  <FormInput
                    type="text"
                    leftIcon="email"
                    placeholder="Search by email..."
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                  />
                </div>

                {/* Copy dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowCopyMenu(!showCopyMenu)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                    Copy
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                  {showCopyMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowCopyMenu(false)} />
                      <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-870 rounded-lg shadow-lg border border-gray-200/70 dark:border-gray-600/70 z-20">
                        <button onClick={() => copyToClipboard('tsv')} className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg">Tab-separated (Excel)</button>
                        <button onClick={() => copyToClipboard('csv')} className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg">CSV</button>
                      </div>
                    </>
                  )}
                </div>

              </div>
            </div>

            {/* Table */}
            {filteredPayments.length > 0 ? (
              <DataTable
                columns={columns}
                data={filteredPayments}
                pagination
                paginationRowsPerPageOptions={[10, 20, 50]}
                paginationComponent={DatatablePagination}
                customStyles={tableStyles}
              />
            ) : (
              <div className="p-12">
                <IconState
                  icon={ExclamationTriangleIcon}
                  title="No results found"
                  description="No payments match your search criteria. Try adjusting your email filter."
                />
              </div>
            )}
          </div>
        </>
      )}

      {/* Calculate Payments Modal */}
      <CalculatePaymentsModal
        open={showCalculateModal}
        onClose={() => setShowCalculateModal(false)}
        onCalculate={handleCalculatePayments}
        isCalculating={isCalculating}
        resources={resourcesWithActiveStatus}
      />
    </div>
  );
}
